import { useMediaQuery, Pagination, Stack, Box, Drawer } from "@mui/material";
import {
  ScanningIndicator,
  formatDateDayjs,
  mobileWidth,
  useScanIndicator,
  useToast
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { isGuid } from "is-guid";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import envConstants from "~/config/envConstants";
import { BatchStatus } from "~/features/batch/batch.type";
import { OrderSidePanel } from "~/features/orders/OrderSidePanel";

import { useDebounce } from "~/hooks/useDebounce";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import { batchStatusFilterAdmin, buildBatchTypeFilter } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetBatchesQuery } from "~/redux/warehouse/batches.hooks";

import AdminBatchTable from "./AdminBatchTable";
import { AdminBatchesSearch } from "./AdminBatchesSearch";
import { AdminBatchesToolbar } from "./AdminBatchesToolbar";
import {
  resetSelectedBatch,
  selectBatch,
  setSidePanelOrderId,
  setPage,
  setToteSearch
} from "./adminBatches.slice";

export type Props = { viewTitle?: ViewNameTranslation };

export function Batches({ viewTitle }: Props) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(mobileWidth, { noSsr: true });
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const navigate = useFcNavigate();
  const [urlSearchParams] = useSearchParams();
  const { setMenuItems, setToolbar, setDatePicker } = useNavbar({
    centerComponent: useMemo(() => <AdminBatchesSearch />, []),
    viewTitle
  });

  const fetchBatchFilters = useAppSelector(
    (state) => state.batch.fetchBatchFilters
  );

  const getLabelsError = useAppSelector((state) => state.home.getLabelsError);
  const page = useAppSelector((state) => state.adminBatches.page);
  const selectedBatch = useAppSelector(
    (state) => state.adminBatches.selectedBatch
  );
  const selectedFulfillmentCenter = useAppSelector(
    selectUsersFulfillmentCenter
  );
  const searchText = useAppSelector((state) => state.adminBatches.searchText);
  const toteSearch = useAppSelector((state) => state.adminBatches.toteSearch);

  const dateParam = urlSearchParams.get("date");
  const selectedDate = useMemo(
    () => (dateParam ? dayjs(dateParam, "YYYY-MM-DD").toDate() : new Date()),
    [dateParam]
  );

  const [scanState, setScanState] = useScanIndicator();
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);

  const sidePanelOrderId = useAppSelector(
    (state) => state.adminBatches.sidePanelOrderId
  );

  const debouncedSearchText = useDebounce(searchText, 250);

  const handleSetDate = useCallback(
    (date: Date | null) => {
      const params = new URLSearchParams();
      const dateString = date ? formatDateDayjs(dayjs(date)) : "";
      params.set("date", dateString);
      navigate(`/admin-batches?${params.toString()}`);
    },
    [navigate]
  );

  useKeyDownHandler();
  useBarcodeScanner({
    findScanMatch: (toteId: string) => {
      setScannedBarcode(toteId);
      setScanState("success");
      if (isGuid(toteId)) {
        dispatch(setToteSearch(toteId));
        return true;
      }

      return false;
    },
    deps: [selectedDate]
  });

  useEffect(() => {
    if (getLabelsError) {
      errorToast(getLabelsError);
    }
  }, [errorToast, getLabelsError]);

  useEffect(() => {
    if (selectedBatch) {
      setToolbar(<AdminBatchesToolbar />);
    } else {
      setToolbar(undefined);
    }
  }, [selectedBatch, setToolbar]);

  useEffect(() => {
    if (envConstants.DEV_CHEATS_UAT) {
      setMenuItems([
        {
          textContent: t("clear selected date"),
          actionCb: () => handleSetDate(null)
        }
      ]);
    }
  }, [setMenuItems, t, handleSetDate]);

  useEffect(() => {
    setDatePicker({ selectedDate, onDateChange: handleSetDate });
  }, [selectedDate, setDatePicker, handleSetDate]);

  useEffect(() => {
    dispatch(setPage(1));
  }, [dispatch, selectedDate]);

  const { startTimeStamp, endTimeStamp } = useMemo(() => {
    if (!selectedDate) {
      return { startTimeStamp: undefined, endTimeStamp: undefined };
    }
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(24, 0, 0, 0);
    return { startTimeStamp: start, endTimeStamp: end };
  }, [selectedDate]);

  const limit = isMobile ? 20 : 40;

  const searchParams = useMemo(() => {
    const defaultStatuses: BatchStatus[] = [
      "Scheduled",
      "Processing",
      "Picked",
      "Dropped",
      "Suspended"
    ];

    return {
      limit,
      offset: (page - 1) * limit,
      status: fetchBatchFilters.selectedBatchStatusesAdmin.length
        ? fetchBatchFilters.selectedBatchStatusesAdmin
        : defaultStatuses,
      ...(selectedDate && { startTimeStamp, endTimeStamp }),
      ...(debouncedSearchText && { search: debouncedSearchText }),
      batchType: fetchBatchFilters.selectedBatchTypes,
      // it would be nice to be able to search multiple temperature zones
      ...(fetchBatchFilters.selectedTempZones.length && {
        temperatureZone: fetchBatchFilters.selectedTempZones[0]
      }),
      ...(fetchBatchFilters.selectedOrderType && {
        orderType: fetchBatchFilters.selectedOrderType
      }),
      ...(toteSearch && {
        toteId: toteSearch,
        searchByCartIdIfToteIdProvided: true
      }),
      isPrioritizeProcessing: true
    };
  }, [
    limit,
    page,
    fetchBatchFilters.selectedBatchStatusesAdmin,
    fetchBatchFilters.selectedBatchTypes,
    fetchBatchFilters.selectedTempZones,
    fetchBatchFilters.selectedOrderType,
    selectedDate,
    startTimeStamp,
    endTimeStamp,
    debouncedSearchText,
    toteSearch
  ]);

  const {
    data: batchResponse,
    error: getBatchesError,
    isFetching: batchesLoading
  } = useGetBatchesQuery(searchParams, { refetchOnMountOrArgChange: true });

  const totalPageCount = batchResponse
    ? Math.ceil(batchResponse.totalBatchCount / limit)
    : 0;

  useEffect(() => {
    if (getBatchesError) {
      errorToast(getMessageFromRtkError(getBatchesError));
    }
  }, [errorToast, getBatchesError]);

  useEffect(() => {
    dispatch(resetSelectedBatch());
    dispatch(setPage(1));
    dispatch(setToteSearch(""));
    dispatch(setSidePanelOrderId(null));
    return () => {
      dispatch(setSidePanelOrderId(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (batchResponse && selectedBatch) {
      const filterSelectedBatch = batchResponse.batches.find(
        (batch) => batch.batchId === selectedBatch.batchId
      );
      if (
        filterSelectedBatch &&
        filterSelectedBatch.status !== selectedBatch.status
      ) {
        dispatch(selectBatch(filterSelectedBatch));
      }
    }
  }, [batchResponse, selectedBatch, dispatch]);

  const availableBatchTypes = buildBatchTypeFilter(selectedFulfillmentCenter);

  const hasFilters =
    !!fetchBatchFilters.selectedTempZones.length ||
    !!(
      fetchBatchFilters.selectedBatchStatusesAdmin.length &&
      fetchBatchFilters.selectedBatchStatusesAdmin.length !==
        batchStatusFilterAdmin.length
    ) ||
    !!(
      fetchBatchFilters.selectedBatchTypes.length &&
      fetchBatchFilters.selectedBatchTypes.length !== availableBatchTypes.length
    );

  return (
    <Box sx={{ maxWidth: "100vw" }}>
      <Stack
        direction="column"
        alignItems="center"
        gap={2}
        mb={6}
        sx={{ paddingTop: "10px" }}
      >
        <AdminBatchTable
          hasFilters={hasFilters}
          batchesLoading={batchesLoading}
          batchesError={
            getBatchesError
              ? getMessageFromRtkError(getBatchesError)
              : undefined
          }
          selectedBatch={selectedBatch}
          unselectBatch={() => dispatch(resetSelectedBatch())}
          selectBatch={(batch) => dispatch(selectBatch(batch))}
        />

        {totalPageCount > 1 && (
          <Pagination
            count={totalPageCount}
            page={page}
            onChange={(_e, p) => {
              dispatch(setPage(p));
            }}
            shape="rounded"
          />
        )}
      </Stack>
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={scannedBarcode}
        placeholderText="Scan Tote"
      />
      <Drawer
        anchor="right"
        open={!!sidePanelOrderId}
        onClose={() => {
          dispatch(setSidePanelOrderId(null));
        }}
      >
        <Box
          sx={{
            maxWidth: "100%",
            width: "700px",
            paddingTop: "48px"
          }}
        >
          {sidePanelOrderId && (
            <OrderSidePanel
              onClose={() => dispatch(setSidePanelOrderId(null))}
              orderId={sidePanelOrderId}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
